import * as React from 'react';
import dayjs from "utils/dayjs";
import { LocalizationProvider, DateCalendar, DigitalClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField, Popover, Box, Typography, IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useConfig from "hooks/useConfig";
// import { toServerDateOnly } from './dateUtils';
import { useTranslation } from 'react-i18next';

const i18nLanguage = process.env.REACT_APP_LANGUAGE || 'ko'; // 예: "zh-CN", "en"
const APP_LOCALE = i18nLanguage.toLowerCase(); // 모두 소문자로 변환 ("zh-cn")
const APP_TIMEZONE = process.env.REACT_APP_TIMEZONE || 'Asia/Seoul';

const CustomDatePicker = ({field, value: propsValue, onChange: propsOnChange, name, setValue, fieldState, ...restProps}) => {
    const { t } = useTranslation();
    const { mode } = useConfig();
    const isDark = mode === 'dark';
    const [anchorEl, setAnchorEl] = React.useState(null);

    // console.log(propsValue)
    // [수정] value 결정 로직: Controller(field)가 있으면 field.value, 없으면 일반 propsValue
    const actualValue = field?.value ?? propsValue;

    // [변경] 시간 없이 날짜만 반환하도록 설정
    // const toServerFormat = (date) => {
    //     return toServerDateOnly(date)
    // };

    const [tempValue, setTempValue] = React.useState(() => {
        if (!actualValue) return null;
        // return dayjs(actualValue).tz(APP_TIMEZONE);
        return dayjs(actualValue); // 타임존 없이 단순 파싱
    });

    React.useEffect(() => {
        const currentVal = propsValue || field?.value;

        // A. 값이 없는 경우 처리
        if (currentVal === undefined || currentVal === null || currentVal === '') {
            setTempValue(null);
            if (setValue && name && currentVal !== null) {
                setValue(name, null);
            }
            return;
        }

        // B. 값 동기화 로직
        const parsed = dayjs(currentVal);
        // const parsed = dayjs(currentVal).tz(APP_TIMEZONE);
        
        if (parsed.isValid()) {
            // 1. 내부 화면 표시용 상태 업데이트
            // setTempValue(parsed);
            // [수정] 현재 상태와 새로 들어온 값이 다를 때만 setTempValue 실행
            // .isSame을 사용하여 불필요한 리렌더링(표시 사라짐) 방지
            if (!tempValue || !parsed.isSame(tempValue, 'day')) {
                setTempValue(parsed);
            }

            // 2. [중요] 날짜 전용 포맷(YYYY-MM-DD)으로 부모 폼 상태 업데이트
            // 이 부분이 실행되어야 react-hook-form의 'required' 에러가 사라집니다.
            const formatted = parsed.format(t('message.common.text.date.format.date'));
            
            if (field?.onChange && field.value !== formatted) {
                field.onChange(formatted);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propsValue, field?.value, name]); 

    const commitChange = (newValue) => {
        const formatted = newValue ? newValue.format('YYYY-MM-DD') : null;
        setTempValue(newValue);

        // 1. Controller(useForm) 환경일 때
        if (field?.onChange) field.onChange(formatted);
        if (setValue && name) setValue(name, formatted, { shouldDirty: true });

        // 2. 일반 환경(useState 등)일 때
        if (propsOnChange) propsOnChange(formatted);
    };

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const open = Boolean(anchorEl);

    // [변경] 날짜 선택 시 바로 커밋하고 팝업 닫기
    const handleDateChange = (newDate) => {
        if (!newDate) return;

        // const updated = newDate.tz(APP_TIMEZONE);
        // const updated = newDate; // 그대로 사용
        const baseDate = tempValue || dayjs();
        const updated = baseDate
            .year(newDate.year())
            .month(newDate.month())
            .date(newDate.date());

        commitChange(updated);
        handleClose(); // 날짜만 있으므로 선택 즉시 닫기
    };

    const colors = {
        bg: isDark ? '#1e1e1e' : '#ffffff',
        text: isDark ? '#ffffff' : '#000000',
        border: isDark ? '#444' : '#ddd'
    };

    const safeDate = tempValue && tempValue.isValid() ? tempValue : dayjs().tz(APP_TIMEZONE);
    
    // const safeDate = tempValue && tempValue.isValid() 
    //     ? tempValue 
    //     : dayjs();
// console.log(tempValue)
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={APP_LOCALE} 
            // localeText={APP_LOCALE === 'zh-cn' ? {
            //     weekDays: ['一','二','三','四','五','六','日']
            // } : undefined}
        >
            <TextField
                {...restProps}
                size="small"
                error={!!fieldState?.error}
                helperText={fieldState?.error?.message}
                // [변경] 포맷에서 시간 제거
                value={tempValue && tempValue.isValid() ? tempValue.format('YYYY.MM.DD') : ''}
                onClick={handleClick}
                autoComplete="off"
                InputProps={{ 
                    readOnly: true,
                    endAdornment: tempValue ? (
                        <InputAdornment position="end" sx={{ m: 0, width: '22px' }}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); commitChange(null); }} sx={{ zIndex:1, p: '1px' }}>
                                <CloseIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </InputAdornment>
                    ) : null
                }}
                sx={{ 
                    width: restProps.style?.width || "100%",
                    cursor: 'pointer',
                    '& .MuiOutlinedInput-root': { bgcolor: isDark ? 'unset' : '#fff', pr: '4px' },
                    '& .MuiOutlinedInput-input': { fontSize: '0.85rem', pr: 0 }
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: { 
                        display: 'flex', 
                        flexDirection: 'row', 
                        bgcolor: `${colors.bg} !important`,
                        border: `1px solid ${colors.border}`,
                        color: colors.text,
                        backgroundImage: 'none'
                    }
                }}
            >
                {/* [변경] 시간 선택 영역(DigitalClock) 삭제 */}
                <Box sx={{ p: 1, bgcolor: colors.bg }}>
                    <DateCalendar 
                        // tempValue가 null이면 선택 표시가 없고, 값이 있으면 해당 날짜에 표시됨
                        // value={tempValue && tempValue.isValid() ? tempValue : null}
                        value={safeDate.startOf('day')}
                        onChange={handleDateChange}
                        // 값이 없을 때 달력이 보여줄 기준 날짜 (오늘)
                        // referenceDate={dayjs().tz(APP_TIMEZONE)}
                        referenceDate={safeDate} // 현재 브라우저 시간 기준 오늘 날짜
                        sx={{
                            '& .MuiTypography-root': { color: colors.text },
                            '& .MuiPickersDay-root': { 
                                color: colors.text,
                                '&.Mui-selected': { bgcolor: '#1976d2 !important', color: '#fff !important' }
                            },
                            // 일요일 빨간색 표시 유지
                            '& .MuiDayCalendar-weekHeader .MuiTypography-root:first-of-type': { color: 'red !important' },
                            '& .MuiPickersDay-root[aria-colindex="1"]': { color: 'red !important' }
                        }}
                    />
                </Box>
            </Popover>
        </LocalizationProvider>
    );
};

export default CustomDatePicker;